import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
  Button,
  Tooltip,
} from "@mui/material";
import {
  Close,
  MusicNote,
  Person,
  AccessTime,
  Link as LinkIcon,
  OpenInNew,
  YouTube,
  Language,
  Bolt as FlashIcon,
  FastForward as FastForwardIcon,
  FilterList,
} from "@mui/icons-material";
import { getImageUrl } from "../../../utils/imageHelper";
import { useSnackbar } from "notistack";

const getUrlIcon = (typeName) => {
  if (!typeName) return <LinkIcon sx={{ fontSize: 16 }} />;
  const t = typeName.toLowerCase();
  if (t.includes("youtube")) return <YouTube sx={{ fontSize: 16, color: "#FF0000" }} />;
  return <Language sx={{ fontSize: 16 }} />;
};

const formatDuration = (duration) => {
  if (!duration) return "—";
  if (typeof duration === "number") {
    const mins = Math.floor(duration / 60000);
    const secs = Math.floor((duration % 60000) / 1000);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }
  return duration;
};

const SongDetailModal = ({ open, onClose, songId, songData }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [song, setSong] = useState(null);
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(false);

  const [activeFlashSession, setActiveFlashSession] = useState(null);
  const [loadingFlashSession, setLoadingFlashSession] = useState(false);
  const [pushingQuestion, setPushingQuestion] = useState(false);
  const [forcePushingQuestion, setForcePushingQuestion] = useState(false);
  const [hasQuestions, setHasQuestions] = useState(false);

  const fetchActiveFlashSession = useCallback(async () => {
    try {
      setLoadingFlashSession(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/live-quiz/flash/active`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.sessions && data.sessions.length > 0) {
          setActiveFlashSession(data.sessions[0]);
        } else {
          setActiveFlashSession(null);
        }
      }
    } catch (error) {
      console.error("Error fetching active flash session:", error);
    } finally {
      setLoadingFlashSession(false);
    }
  }, []);

  const checkQuestions = useCallback(
    async (sid) => {
      if (!sid) return;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/quiz/song/${sid}?difficulty_id=all&questions_per_song=1`
        );
        if (res.ok) {
          const data = await res.json();
          setHasQuestions(data.questions && data.questions.length > 0);
        } else {
          setHasQuestions(false);
        }
      } catch {
        setHasQuestions(false);
      }
    },
    []
  );

  const pushFlashQuestion = async () => {
    if (!activeFlashSession || !songId) return;
    try {
      setPushingQuestion(true);
      const rawRapid = activeFlashSession?.rapid_config;
      const parsedRapid =
        typeof rawRapid === "string"
          ? (() => {
              try {
                return JSON.parse(rawRapid);
              } catch {
                return {};
              }
            })()
          : rawRapid || {};
      const sessionDelay =
        typeof parsedRapid.delay_seconds === "number"
          ? parsedRapid.delay_seconds
          : 10;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/live-quiz/flash/sessions/${activeFlashSession.session_id}/push-song/${songId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
          body: JSON.stringify({ delay_seconds: sessionDelay }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to push question");
      }
      await response.json();
      enqueueSnackbar(`Question pushed! Delay: ${sessionDelay}s`, {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to push question", {
        variant: "error",
      });
    } finally {
      setPushingQuestion(false);
    }
  };

  const handleForceClick = async () => {
    if (!songId) return;
    try {
      setForcePushingQuestion(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/quiz/force-push/${songId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to force push question");
      }
      enqueueSnackbar(data.message || "Question pushed to logged-in users", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to force push", {
        variant: "error",
      });
    } finally {
      setForcePushingQuestion(false);
    }
  };

  const fetchDetails = useCallback(async () => {
    if (!songId) return;
    setLoading(true);
    try {
      const [songRes, urlsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/songs/${songId}`),
        fetch(
          `${process.env.REACT_APP_API_URL}/api/song-urls/urls?songs_master_id=${songId}`
        ),
      ]);
      const songDetail = await songRes.json();
      const urlsData = await urlsRes.json();
      setSong(songDetail);
      setUrls(Array.isArray(urlsData) ? urlsData : []);
    } catch (err) {
      console.error("Error fetching song details:", err);
    } finally {
      setLoading(false);
    }
  }, [songId]);

  useEffect(() => {
    if (open && songId) {
      fetchDetails();
      fetchActiveFlashSession();
      checkQuestions(songId);
    }
    if (!open) {
      setSong(null);
      setUrls([]);
      setActiveFlashSession(null);
      setHasQuestions(false);
    }
  }, [open, songId, fetchDetails, fetchActiveFlashSession, checkQuestions]);

  const displayName = song?.name || songData?.name || songData?.song_name || "Unknown Song";
  const displayArtist = song?.artist || songData?.artist || songData?.artist_name || "Unknown Artist";
  const displayImage = song?.image || songData?.image || songData?.song_image;
  const displayDuration = song?.duration || songData?.duration;
  const displayAlbum = song?.album || songData?.album;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1A1A1A",
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.08)",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {loading && !song ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: "#1DB954" }} />
          </Box>
        ) : (
          <>
            {/* Header image + close */}
            <Box sx={{ position: "relative" }}>
              <Box
                component="img"
                src={getImageUrl(displayImage)}
                alt={displayName}
                sx={{
                  width: "100%",
                  height: 200,
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(transparent 40%, rgba(26,26,26,0.95) 100%)",
                }}
              />
              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                }}
                size="small"
              >
                <Close fontSize="small" />
              </IconButton>

              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 20,
                  right: 20,
                }}
              >
                <Typography
                  sx={{ fontSize: "1.3rem", fontWeight: 700, lineHeight: 1.2 }}
                >
                  {displayName}
                </Typography>
              </Box>
            </Box>

            {/* Details */}
            <Box sx={{ px: 2.5, py: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Person
                    sx={{ fontSize: 18, color: "rgba(255,255,255,0.35)" }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "rgba(255,255,255,0.35)",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Artist
                    </Typography>
                    <Typography sx={{ fontSize: "0.9rem" }}>
                      {displayArtist}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <AccessTime
                    sx={{ fontSize: 18, color: "rgba(255,255,255,0.35)" }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "rgba(255,255,255,0.35)",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Duration
                    </Typography>
                    <Typography sx={{ fontSize: "0.9rem" }}>
                      {formatDuration(displayDuration)}
                    </Typography>
                  </Box>
                </Box>

                {displayAlbum && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <MusicNote
                      sx={{ fontSize: 18, color: "rgba(255,255,255,0.35)" }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "rgba(255,255,255,0.35)",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Album
                      </Typography>
                      <Typography sx={{ fontSize: "0.9rem" }}>
                        {displayAlbum}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Flash Quiz Push/Force */}
              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.06)" }} />

              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "rgba(156,39,176,0.06)",
                  border: "1px solid rgba(156,39,176,0.25)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <FlashIcon
                    sx={{ fontSize: 18, color: "#9C27B0" }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "rgba(255,255,255,0.5)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    Flash Quiz
                  </Typography>
                </Box>

                {loadingFlashSession ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: "#9C27B0" }} />
                    <Typography
                      sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.35)" }}
                    >
                      Loading session...
                    </Typography>
                  </Box>
                ) : activeFlashSession ? (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "rgba(255,255,255,0.4)",
                        mb: 1.5,
                      }}
                    >
                      Session: <strong style={{ color: "rgba(255,255,255,0.7)" }}>{activeFlashSession.quiz_name}</strong>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          pushingQuestion ? (
                            <CircularProgress size={14} sx={{ color: "#fff" }} />
                          ) : (
                            <FlashIcon sx={{ fontSize: 16 }} />
                          )
                        }
                        onClick={pushFlashQuestion}
                        disabled={pushingQuestion}
                        sx={{
                          backgroundColor: "#9C27B0",
                          textTransform: "none",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          borderRadius: 1.5,
                          px: 2,
                          "&:hover": { backgroundColor: "#7B1FA2" },
                        }}
                      >
                        {pushingQuestion ? "Pushing..." : "Push"}
                      </Button>

                      {hasQuestions && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={
                            forcePushingQuestion ? (
                              <CircularProgress size={14} sx={{ color: "#ff9800" }} />
                            ) : (
                              <FastForwardIcon sx={{ fontSize: 16 }} />
                            )
                          }
                          onClick={handleForceClick}
                          disabled={forcePushingQuestion}
                          sx={{
                            borderColor: "#ff9800",
                            color: "#ff9800",
                            textTransform: "none",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            borderRadius: 1.5,
                            px: 2,
                            "&:hover": {
                              borderColor: "#f57c00",
                              backgroundColor: "rgba(255,152,0,0.08)",
                            },
                          }}
                        >
                          {forcePushingQuestion ? "Pushing..." : "Force"}
                        </Button>
                      )}

                      <Tooltip title="Refresh session">
                        <IconButton
                          onClick={fetchActiveFlashSession}
                          size="small"
                          sx={{ color: "rgba(255,255,255,0.3)" }}
                        >
                          <FilterList sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "rgba(255,255,255,0.3)",
                    }}
                  >
                    No active flash quiz session
                  </Typography>
                )}
              </Box>

              {/* Song URLs */}
              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.06)" }} />

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <LinkIcon
                    sx={{ fontSize: 18, color: "rgba(255,255,255,0.35)" }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "rgba(255,255,255,0.35)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    Song URLs
                  </Typography>
                  <Chip
                    label={urls.length}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      backgroundColor: "rgba(255,255,255,0.06)",
                      color: "rgba(255,255,255,0.4)",
                    }}
                  />
                </Box>

                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} sx={{ color: "#1DB954" }} />
                  </Box>
                ) : urls.length === 0 ? (
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "rgba(255,255,255,0.25)",
                      textAlign: "center",
                      py: 2,
                    }}
                  >
                    No URLs available
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {urls.map((url, idx) => (
                      <Box
                        key={url.song_url_id || idx}
                        component="a"
                        href={url.song_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          backgroundColor: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.06)",
                          textDecoration: "none",
                          color: "inherit",
                          transition: "all 0.15s",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.06)",
                            borderColor: "rgba(29,185,84,0.3)",
                          },
                        }}
                      >
                        {getUrlIcon(url.url_type_name)}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              color: "#1DB954",
                            }}
                          >
                            {url.url_type_name || "Link"}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "rgba(255,255,255,0.35)",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {url.song_url}
                          </Typography>
                        </Box>
                        <OpenInNew
                          sx={{
                            fontSize: 14,
                            color: "rgba(255,255,255,0.25)",
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SongDetailModal;
